import React, { useEffect, useState } from 'react';
import './AdminReportsPage.scss';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { useTranslation } from 'react-i18next';
import { confirmDialog, ConfirmDialog, ConfirmDialogReturn } from 'primereact/confirmdialog';
import { format } from 'date-fns';
import { downloadReport, getReports } from '../../../shared/services/pricing/PricingService';
import { BillingReport } from '../../../shared/models/pricing/BillingReport';
import Loader from '../../../shared/components/common/loader/Loader';
import { Button } from 'primereact/button';
import DownloadBillingReport from '../../../shared/components/common/download-billing-report/DownloadBillingReport';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../../shared/utils/mobile';

const AdminReportsPage: React.FC = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const [downloadingReportIds, setDownloadingReportIds] = useState<string[]>([]);

  let reportRef: ConfirmDialogReturn;

  const hide = () => {
    if (reportRef) {
      reportRef.hide();
      refresh();
    }
  };
  const [tableData, setTableData] = useState<BillingReport[] | null>(null);

  useEffect(() => {
    refresh();
  }, []);

  const refresh = () => getReports().then((reports) => setTableData(reports));

  const reportDialog = () => {
    reportRef = confirmDialog({
      message: <DownloadBillingReport reportEndCallback={() => hide()} />,
      header: 'Generate new report',
      footer: () => null,
    });
  };

  const download = async (id: string) => {
    if (downloadingReportIds.includes(id)) {
      return;
    }

    try {
      setDownloadingReportIds((prev) => [...prev, id]);
      const url = await downloadReport(id);
      if (url) {
        window.open(url, '_blank');
      } else {
        console.error('No url');
      }
    } finally {
      setDownloadingReportIds((prev) => prev.filter((reportId) => reportId !== id));
    }
  };

  if (!tableData) {
    return <Loader />;
  }

  const actionBodyTemplate = (data: BillingReport) => {
    const reportId = data.reportId;
    const isThisDownloading = downloadingReportIds.includes(reportId);
    return (
      <Button className="action-btn" onClick={() => download(reportId)}>
        {isThisDownloading ? (
          <span className="ico--loader ico--xs" />
        ) : (
          <span className="ico--download ico--xs" />
        )}
      </Button>
    );
  };

  return (
    <div className="admin-reports">
      <ConfirmDialog className="dialog-box" />

      <div className="admin-reports__action-panel">
        <Button className="btn btn--sm" label="Generate new report" onClick={() => reportDialog()} />
      </div>
      <DataTable
        stripedRows
        paginator
        paginatorClassName="pagination"
        removableSort
        sortField="id"
        sortOrder={1}
        rows={50}
        rowsPerPageOptions={[10, 25, 50]}
        value={tableData}
        tableStyle={{ minWidth: '50rem' }}
        tableClassName="table"
        className="table-wrapper table-wrapper--stack admin-reports__table"
        responsiveLayout="stack"
        currentPageReportTemplate={t('rowsNumber') + ':'}
        paginatorTemplate={
          isMobile
            ? MOBILE_PAGINATOR_TEMPLATE
            : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
        }
        pageLinkSize={isMobile ? 3 : 5}
      >
        <Column field="id" header="ID" headerClassName="table__header" style={{ minWidth: '30px' }} />
        <Column
          field="type"
          header="Report type"
          headerClassName="table__header"
          bodyClassName="table__cell--stack-title"
          style={{ minWidth: '45px' }}
        />
        <Column
          header="Range"
          headerClassName="table__header "
          bodyClassName="table__cell--stack-wide"
          body={(data) => `${format(data.from, 'yyyy-MM-dd')} - ${format(data.to, 'yyyy-MM-dd ')} `}
          style={{ width: '250px' }}
        />

        <Column
          header="Created"
          body={(data) => format(data.created, 'yyyy-MM-dd / HH:mm')}
          headerClassName="table__header "
          style={{ width: '380px' }}
        />
        <Column
          header="Actions"
          body={actionBodyTemplate}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center table__cell--stack-actions"
          style={{ width: '80px' }}
        />
      </DataTable>
    </div>
  );
};

export default AdminReportsPage;
